import { useEffect } from "react";
import JacksonvilleHeroSection from "./components/HeroSection";
import JacksonvilleStickyNav from "./components/StickyNav";
import JacksonvilleProcessingTimeSection from "./components/ProcessingTimeSection";
import JacksonvilleServicesSection from "./components/ServicesSection";
import JacksonvilleLocalAreaSection from "./components/LocalAreaSection";
import JacksonvilleTestimonialsSection from "./components/TestimonialsSection";
import JacksonvilleAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import JacksonvilleComparisonSection from "./components/ComparisonSection";
import JacksonvilleMistakesSection from "./components/MistakesSection";
import JacksonvilleHowItWorksSection from "./components/HowItWorksSection";
import JacksonvilleFAQSection, { faqs } from "./components/FAQSection";
import JacksonvilleHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import JacksonvilleFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import JacksonvilleTravelTimingSection from "./components/TravelTimingSection";
import JacksonvilleDestinationsSection from "./components/DestinationsSection";

export default function JacksonvillePage() {
  useEffect(() => {
    document.title = "Emergency Passport Jacksonville, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/jacksonville/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Jacksonville, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Northeast Florida military families, business travelers, cruise passengers, and students.");
    setMeta("geo.placename", "Jacksonville, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "30.3322;-81.6557");
    setMeta("ICBM", "30.3322, -81.6557");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Jacksonville Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Jacksonville — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Jacksonville and Northeast Florida travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Jacksonville",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Jacksonville",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <JacksonvilleHeroSection />
      <JacksonvilleStickyNav />
      <JacksonvilleProcessingTimeSection />
      <JacksonvilleServicesSection />
      <JacksonvilleLocalAreaSection />
      <JacksonvilleTravelTimingSection />
      <JacksonvilleDestinationsSection />
      <JacksonvilleTestimonialsSection />
      <JacksonvilleAcceptanceFacilitiesSection />
      <JacksonvilleComparisonSection />
      <JacksonvilleMistakesSection />
      <JacksonvilleHowItWorksSection />
      <JacksonvilleFAQSection />
      <JacksonvilleHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/jacksonville" state="FL" />
      <JacksonvilleFinalCTA />
    </div>
  );
}